import { Link } from 'react-router';
import { cx } from '../lib/cx';
import { Badge } from './Badge';
import { Button } from './Button';
import { IconLock } from '../components/icons';

export interface WorkoutCardProps {
  title: string;
  to: string;
  exercisesLabel: string;
  durationLabel?: string;
  weekDayLabel?: string;
  status?: 'planned' | 'done' | 'missed';
  statusLabel?: string;
  /** Trening iza probnog pristupa: kartica ne vodi nikamo, nego nudi kupnju. */
  locked?: boolean;
  lockedLabel?: string;
  unlockLabel?: string;
  onUnlock?: () => void;
  className?: string;
}

const STATUS_TONE = { planned: 'neutral', done: 'success', missed: 'warning' } as const;

/*
  Do 400 px naslov i akcija stoje JEDNO ISPOD DRUGOG.

  Rame uz rame kartica ne moze uze od ~371 px: naslov je `truncate`, ali s
  `white-space: nowrap` njegov doprinos min-content sirini ostaje puna duljina
  teksta, pa se ne da stisnuti. Zbog toga se na 320 i 360 px — iPhone SE, mini,
  vecina Androida — vukla cijela stranica postrance. Slaganje u stupac mice
  uzrok umjesto da ga skriva `overflow: hidden`.
*/
const SHELL =
  'group flex flex-col items-stretch gap-3 rounded-xl border border-line-subtle ' +
  'bg-surface p-5 shadow-card transition-shadow duration-200 ' +
  'min-[400px]:flex-row min-[400px]:items-center min-[400px]:justify-between min-[400px]:gap-4';

export function WorkoutCard({
  title,
  to,
  exercisesLabel,
  durationLabel,
  weekDayLabel,
  status = 'planned',
  statusLabel,
  locked = false,
  lockedLabel,
  unlockLabel,
  onUnlock,
  className,
}: WorkoutCardProps) {
  const body = (
    <span className="min-w-0">
      {weekDayLabel && <span className="eyebrow mb-1 block">{weekDayLabel}</span>}
      <span className="block truncate font-display text-[21px] font-semibold text-ink">{title}</span>
      <span className="mt-1 block text-[13px] text-ink-muted">
        {exercisesLabel}
        {durationLabel ? ` · ${durationLabel}` : ''}
      </span>
    </span>
  );

  if (locked) {
    return (
      <div
        data-testid="locked-workout-card"
        className={cx(SHELL, 'border-dashed bg-bg-alt', className)}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span aria-hidden="true" className="shrink-0 text-ink-muted">
            <IconLock width={20} height={20} />
          </span>
          {body}
        </span>
        <span className="flex shrink-0 flex-col items-start gap-2 min-[400px]:items-end">
          {lockedLabel && <Badge tone="neutral">{lockedLabel}</Badge>}
          {unlockLabel && onUnlock && (
            <Button size="sm" variant="secondary" onClick={onUnlock}>
              {unlockLabel}
            </Button>
          )}
        </span>
      </div>
    );
  }

  return (
    <Link to={to} className={cx(SHELL, 'hover:shadow-card-raised', className)}>
      {body}
      <span className="flex shrink-0 items-center justify-between gap-3 min-[400px]:justify-end">
        {statusLabel && <Badge tone={STATUS_TONE[status]}>{statusLabel}</Badge>}
        <span
          aria-hidden="true"
          className="text-accent transition-transform group-hover:translate-x-1"
        >
          →
        </span>
      </span>
    </Link>
  );
}
